import { staticPath } from "faqSrc/lib/$path";
import useTranslation from "next-translate/useTranslation";
import React, { useEffect, useState, VFC } from "react";
import styled from "styled-components";
import Image from "~/components/atoms/Image";
import TocActions from "~/components/modules/qaFixedHeader/TocActions";
import TocContent from "~/components/modules/toc/TocContent";
import { QuestionAnswer } from "~/models/questionAnswer";
import { unstyledButton } from "~/styles/CommonStyles";

export interface HeaderTocProps {
  increaseFeedbackHelpfulUrl?: string;
  increaseFeedbackHelpfulCallback?: () => void;
  isHeaderShown?: boolean;
  lineShareUrl: string;
  questionAnswer: QuestionAnswer;
  twitterShareUrl: string;
}

const tocId = "qa-header-toc";

const HeaderToc: VFC<HeaderTocProps> = ({
  increaseFeedbackHelpfulCallback,
  isHeaderShown,
  lineShareUrl,
  questionAnswer,
  twitterShareUrl,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (typeof isHeaderShown === "undefined" || !isOpen) {
      return;
    }
    setIsOpen(isHeaderShown);
  }, [isHeaderShown, isOpen]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const links = document.querySelectorAll(`#${tocId} a`);

    links.forEach((link) => link.addEventListener("click", onLinkClick));

    function onLinkClick(e: Event) {
      e.stopPropagation();
      setIsOpen(false);
    }

    return () => {
      links.forEach((link) => link.removeEventListener("click", onLinkClick));
    };
  }, []);

  const onTocButtonClick = () => {
    setIsOpen(!isOpen);
  };

  const onBackdropClick = () => {
    setIsOpen(false);
  };

  const { t } = useTranslation();

  return (
    <>
      <QaHeaderTocButton onClick={onTocButtonClick}>
        {t("common:right-sidebar-heading")}
        <QaHeaderTocArrowIcon className={isOpen ? "open" : undefined}>
          <Image
            width="12"
            height="12"
            alt="icon arrow"
            src={staticPath.img.icon_arrow_down_gray_svg}
            layout="fixed"
            ampLayout="fixed"
          />
        </QaHeaderTocArrowIcon>
      </QaHeaderTocButton>
      <TocContainer id={tocId} className={isOpen ? "open" : undefined}>
        <QaHeaderTocCard>
          <TocActions
            increaseFeedbackHelpfulCallback={increaseFeedbackHelpfulCallback}
            lineShareUrl={lineShareUrl}
            questionAnswerId={questionAnswer.id}
            topicId={questionAnswer.topicId}
            twitterShareUrl={twitterShareUrl}
          />
          <TocContent
            answerTableOfContentHtml={questionAnswer.answerTableOfContentHtml}
          />
        </QaHeaderTocCard>
        <QaHeaderTocBackdrop onClick={onBackdropClick} />
      </TocContainer>
    </>
  );
};

export const QaHeaderTocButton = styled.button`
  ${unstyledButton};
  align-items: center;
  color: var(--cap-clr);
  display: flex;
  font-size: 14px;
  font-weight: 400;
`;

export const QaHeaderTocArrowIcon = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: 8px;
  transition: transform 0.2s;

  &.open {
    transform: rotate(180deg);
  }
`;

export const QaHeaderTocBackdrop = styled.div`
  height: 100vh;
  inset: 0;
  position: fixed;
  width: 100%;
  z-index: -1;
`;

export const QaHeaderTocCard = styled.div`
  background-color: var(--body-bg-clr);
  border-radius: 8px;
  box-shadow: var(--toc-box-shadow);
  max-height: calc(100vh - 120px);
  max-width: 350px;
  overflow-y: auto;
  padding: 16px;
  position: fixed;
  right: 16px;
  top: 50px;
  width: 100%;
`;

const TocContainer = styled.div`
  display: none;
  position: fixed;

  &.open {
    display: block;
  }
`;

export default HeaderToc;
